import { useQuery } from "@tanstack/react-query";
import { useDatabase } from "@rapid/providers/database/DatabaseProvider";
import type { DatabaseAdapter } from "@rapid/providers/database/DatabaseAdapter";
import type { DatasetData, BatchData, SourceDocument } from "@rapid/utils/api/datasets";
import * as datasetsApi from "@rapid/utils/api/datasets";

export const datasetKeys = {
    all: ["datasets"] as const,
    byRapidDataId: (workspaceId: string, rapidDataId: string) =>
        [...datasetKeys.all, "byRapidDataId", workspaceId, rapidDataId] as const,
    batch: (datasetId: string, batchName: string) =>
        [...datasetKeys.all, "batch", datasetId, batchName] as const,
    sources: (datasetId: string, batchId: string) =>
        [...datasetKeys.all, "sources", datasetId, batchId] as const
};

/**
 * Query the dataset by rapidDataId directly from Firestore.
 * Shared between the react-query hook and imperative callers.
 *
 * Filters by both owner and rapidDataId — Firestore rules require an owner
 * filter on the datasets collection, so a rapidDataId-only query is rejected
 * with permission-denied.
 */
export async function queryDatasetByRapidDataId(
    database: DatabaseAdapter,
    workspaceId: string,
    rapidDataId: string
): Promise<DatasetData | null> {
    const docs = await database.queryDocs(
        "datasets",
        { field: "owner", op: "==", value: workspaceId },
        { field: "rapidDataId", op: "==", value: rapidDataId }
    );
    if (docs.length === 0) return null;
    const doc = docs[0];
    return { ...doc.data(), id: doc.id } as DatasetData;
}

export function useDatasetByRapidDataId(
    workspaceId: string | undefined,
    rapidDataId: string | undefined
) {
    const database = useDatabase();
    const query = useQuery<DatasetData | null>({
        queryKey: datasetKeys.byRapidDataId(workspaceId!, rapidDataId!),
        queryFn: () => queryDatasetByRapidDataId(database, workspaceId!, rapidDataId!),
        enabled: !!workspaceId && !!rapidDataId,
        staleTime: Infinity,
        // Poll every 2s while the dataset hasn't been created yet (async first-model setup)
        refetchInterval: (query) => (query.state.data === null ? 2000 : false)
    });
    return query;
}

export function useBatchByName(datasetId: string | undefined, batchName: string) {
    return useQuery<BatchData | null>({
        queryKey: datasetKeys.batch(datasetId!, batchName),
        queryFn: () => datasetsApi.getBatchByName(datasetId!, batchName),
        enabled: !!datasetId,
        staleTime: Infinity,
        // Poll every 2s while the batch hasn't been created yet (created async by useReviewBatchCount)
        refetchInterval: (query) => (query.state.data === null ? 2000 : false)
    });
}

export function useSourcesInBatch(datasetId: string | undefined, batchId: string | undefined) {
    return useQuery<SourceDocument[]>({
        queryKey: datasetKeys.sources(datasetId!, batchId!),
        queryFn: () => datasetsApi.listSourcesInBatch(datasetId!, batchId!),
        enabled: !!datasetId && !!batchId,
        staleTime: Infinity
    });
}
